import { useState } from "react";
import { SectionCard } from "shared/app/SectionCard";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { useFormTyped } from "shared/hooks";
import { Row } from "views/applications-id-report/components/Row";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { Unit } from "types/landlord/address";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { instance } from "api/apiBase";
import { useAuthContext } from "providers/Auth";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { PMSValue } from "shared/app/RentalInfoForm/components/PmsValue";
import { useEditDetailsPermissions } from "shared/hooks/use-edit-details-permissions";

// CRM-1167. Both fields are free-text label lists the PMS owns (Rent Manager
// spells them "12 Months", "Sewage"), so the editor is a comma-separated line
// rather than a fixed option list — a hard-coded set would silently drop
// whatever wording a partner actually uses.
const SECTION_TITLE = "Lease terms & utilities";

const toList = (raw: string): string[] => [
  ...new Set(
    raw
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean)
  )
];

const toInput = (list: string[] | null | undefined): string =>
  (list ?? []).join(", ");

const ListValue = ({ items }: { items: string[] | null | undefined }) => {
  if (!items?.length) return <p className="font-medium text-content-1000">-</p>;

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <Badge key={item} variant={BadgeVariant.BASE} size={BadgeSize.SM}>
          {item}
        </Badge>
      ))}
    </div>
  );
};

type ListField = "lease_terms" | "included_utilities";

const FIELDS: { key: ListField; label: string; placeholder: string }[] = [
  {
    key: "lease_terms",
    label: "Lease terms",
    placeholder: "6 Months, 12 Months"
  },
  {
    key: "included_utilities",
    label: "Included utilities",
    placeholder: "Water, Sewage"
  }
];

interface FormValues {
  lease_terms: string;
  included_utilities: string;
  pms_overrides: Partial<Record<ListField, boolean>>;
}

const EditMarketingInfoForm = ({
  onClose,
  initialValues,
  unit,
  isPmsUnit
}: {
  onClose: () => void;
  initialValues: FormValues;
  unit: Unit;
  isPmsUnit: boolean;
}) => {
  const queryClient = useQueryClient();
  const { register, onSubmit, values } = useFormTyped(initialValues);
  const overrides = values.pms_overrides;

  const { mutate: save, isPending } = useMutation({
    mutationFn: () =>
      instance.patch(`/portal/v1/units/${unit.id}`, {
        lease_terms: toList(values.lease_terms),
        included_utilities: toList(values.included_utilities),
        ...(isPmsUnit && {
          pms_overrides: {
            ...unit.pms_overrides,
            ...values.pms_overrides
          }
        })
      }),
    onSuccess: () => {
      toast.success(`${SECTION_TITLE} updated successfully`);
      queryClient.invalidateQueries({
        queryKey: ["unit", String(unit.property.id), String(unit.id)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(
        `Failed to update ${SECTION_TITLE}: ${getErrorMessage(error)}`
      );
    }
  });

  const handleSubmit = onSubmit({ onSuccess: () => save() });

  // Switching sync back ON mirrors the PMS list into the field — including the
  // empty case — so the form never shows a manual value that contradicts the
  // "PMS Value" line next to it.
  const handleOverrideChange = (field: ListField, synced: boolean) => {
    register("pms_overrides").onChange({ ...overrides, [field]: synced });
    if (synced) register(field).onChange(toInput(unit.pms_values?.[field]));
  };

  return (
    <SectionCard
      title={SECTION_TITLE}
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveDisabled={isPending}
          containerClassName="flex gap-2 pt-6"
        />
      }
    >
      <div className="space-y-8">
        {FIELDS.map(({ key, label, placeholder }) => {
          const synced = overrides?.[key] ?? true;

          return (
            <div key={key} className="grid grid-cols-2 gap-4 items-start">
              <div>
                <BaseInput
                  id={key}
                  label={label}
                  placeholder={placeholder}
                  value={register(key).value ?? ""}
                  onChange={(val) => register(key).onChange(val)}
                  error={register(key).error}
                  readOnly={isPmsUnit && synced}
                />
                <Show if={isPmsUnit}>
                  <PmsSyncOption
                    value={synced}
                    onChange={(val) => handleOverrideChange(key, val)}
                    name={key}
                  />
                </Show>
              </div>
              <Show if={isPmsUnit}>
                <PMSValue
                  value={
                    unit.pms_values?.[key]?.length
                      ? unit.pms_values[key].join(", ")
                      : undefined
                  }
                />
              </Show>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

interface MarketingInfoSectionProps {
  unit: Unit;
}

export const MarketingInfoSection = ({ unit }: MarketingInfoSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { type } = useAuthContext();
  const { canEditUnitDetails, unitEditDeniedTooltip } =
    useEditDetailsPermissions();
  const isPmsUnit = type.isPmsListingSyncEnabled && !!unit.pms_id;

  if (isEditing) {
    const syncedValue = (field: ListField) =>
      isPmsUnit && (unit.pms_overrides?.[field] ?? true)
        ? toInput(unit.pms_values?.[field])
        : toInput(unit[field]);

    return (
      <EditMarketingInfoForm
        onClose={() => setIsEditing(false)}
        isPmsUnit={isPmsUnit}
        initialValues={{
          lease_terms: syncedValue("lease_terms"),
          included_utilities: syncedValue("included_utilities"),
          pms_overrides: {
            lease_terms: unit.pms_overrides?.lease_terms ?? true,
            included_utilities: unit.pms_overrides?.included_utilities ?? true
          }
        }}
        unit={unit}
      />
    );
  }

  return (
    <SectionCard
      title={SECTION_TITLE}
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
          disabled={!canEditUnitDetails}
          tooltip={unitEditDeniedTooltip}
          tooltipPlacement="bottom-end"
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4 w-full">
        {FIELDS.map(({ key, label }) => (
          <Row
            key={key}
            title={label}
            value={<ListValue items={unit[key]} />}
            valueClassName="w-[500px]"
          />
        ))}
      </div>
    </SectionCard>
  );
};
